/**
 * Grand Quack Auto — objective system.
 *
 * Three sequential objectives that guide the player through the core loop:
 * scare NPCs → explore the city → build chaos. Completing all three triggers
 * "LEVEL COMPLETE!" — the vertical slice achievement.
 *
 * See research.md §5 session 5 prompt.
 */

import type { System, World } from '../types';
import { getComboCount } from './comboSystem';
import { getCurrentTierIndex } from './duckEvolutionSystem';
import { getTotalScared } from './npcSystem';
import { isDeathActive } from '../ui/deathScreen';
import { showStatsScreen } from '../ui/statsScreen';
import { parkPositions } from '../world/cityGenerator';

// ── Objective definitions ─────────────────────────────────────────────────

interface Objective {
  description: string;
  check: (world: World) => boolean;
  completed: boolean;
}

function isNearPark(world: World): boolean {
  if (parkPositions.length === 0) return true; // edge case: no parks → auto-complete
  const duck = world.duck.position;
  for (const park of parkPositions) {
    if (duck.distanceTo(park) < 5) return true;
  }
  return false;
}

const objectives: Objective[] = [
  { description: 'Scare 3 pedestrians', check: () => getTotalScared() >= 3, completed: false },
  { description: 'Waddle to the park', check: (w) => isNearPark(w), completed: false },
  { description: 'Reach 50 Chaos', check: (w) => w.chaos.points >= 50, completed: false },
  { description: 'Hijack a car', check: (w) => w.playerMode === 'driving', completed: false },
  { description: 'Reach ★★ Menace', check: (w) => w.chaos.wanted >= 2, completed: false },
  { description: 'Evolve to Quackie Chan', check: () => getCurrentTierIndex() >= 3, completed: false },
  { description: 'Chain a x4 combo', check: () => getComboCount() >= 4, completed: false },
  { description: 'Become DUCK NORRIS', check: () => getCurrentTierIndex() >= 6, completed: false },
];

// ── Module state ──────────────────────────────────────────────────────────

let currentIndex = 0;
let allComplete = false;
let lastCompletedAt = -Infinity;

// ── Exports for HUD ───────────────────────────────────────────────────────

export function getCurrentObjective(): Objective | null {
  if (allComplete || currentIndex >= objectives.length) return null;
  return objectives[currentIndex]!;
}

export function isAllComplete(): boolean {
  return allComplete;
}

/** True for 1.5 seconds after an objective completes (for flash effect). */
export function justCompleted(elapsed: number): boolean {
  return elapsed - lastCompletedAt < 1.5;
}

// ── System ────────────────────────────────────────────────────────────────

export const objectiveSystem: System = {
  name: 'objective',
  fixedUpdate(_dt: number, world: World) {
    if (allComplete) return;
    if (currentIndex >= objectives.length) {
      allComplete = true;
      return;
    }

    const obj = objectives[currentIndex]!;
    if (!obj.completed && obj.check(world)) {
      obj.completed = true;
      lastCompletedAt = world.time.elapsed;
      currentIndex++;
      if (currentIndex >= objectives.length) {
        allComplete = true;
        // Show end-of-run stats after a brief delay (skip if death screen took over).
        setTimeout(() => { if (!isDeathActive()) showStatsScreen(world); }, 2000);
      }
    }
  },
};
